import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, Brain, Lightbulb, Bell, Wine, AlertTriangle, Moon, LogOut, Menu, X, Telescope, Bookmark, Upload, Beer } from "lucide-react";
import { adminLogout, isAdminAuthed } from "../../lib/adminApi";
import BenoLogo from "../../components/BenoLogo";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true, testid: "admin-nav-dashboard" },
  { to: "/admin/menu", label: "Gin Menu", icon: BookOpen, testid: "admin-nav-menu" },
  { to: "/admin/beverages", label: "Spirits Menu", icon: Beer, testid: "admin-nav-beverages" },
  { to: "/admin/menu-upload", label: "Menu Upload", icon: Upload, testid: "admin-nav-menu-upload" },
  { to: "/admin/enrichment", label: "Enrichment", icon: Telescope, testid: "admin-nav-enrichment" },
  { to: "/admin/sources", label: "Sources", icon: Bookmark, testid: "admin-nav-sources" },
  { to: "/admin/intelligence", label: "Intelligence", icon: Brain, testid: "admin-nav-intelligence" },
  { to: "/admin/recommendations", label: "Recs", icon: Lightbulb, testid: "admin-nav-recs" },
  { to: "/admin/menu-gaps", label: "Gaps", icon: AlertTriangle, testid: "admin-nav-gaps" },
  { to: "/admin/alerts", label: "Alerts", icon: Bell, testid: "admin-nav-alerts" },
  { to: "/admin/flights", label: "Flights", icon: Wine, testid: "admin-nav-flights" },
  { to: "/admin/tonight", label: "Tonight", icon: Moon, testid: "admin-nav-tonight" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (!isAdminAuthed()) return <Navigate to="/admin/login" replace />;

  const logout = () => { adminLogout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen flex bg-[#0E0F0C]" data-testid="admin-shell">
      {/* Side rail */}
      <aside className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-[#101113] border-r border-white/8 transform ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform`}
             data-testid="admin-sidebar">
        <div className="px-6 py-6 border-b border-white/5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <BenoLogo size={40} />
            <p className="overline text-[#C17F59] mt-1">Liquid Control Room</p>
          </Link>
          <button onClick={() => setOpen(false)} className="md:hidden text-white/60"><X size={18} /></button>
        </div>
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.exact}
                     onClick={() => setOpen(false)}
                     className={({ isActive }) =>
                       `flex items-center gap-3 px-3 py-2.5 text-xs uppercase tracking-[0.22em] transition-colors ${
                         isActive ? "bg-[#C17F59]/15 text-[#C17F59] border-l-2 border-[#C17F59]" : "text-white/60 hover:text-white hover:bg-white/4"
                       }`}
                     data-testid={n.testid}>
              <n.icon size={16} strokeWidth={1.4} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 mt-2 border-t border-white/5 absolute bottom-0 left-0 right-0">
          <Link to="/" className="block text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-[#C17F59] py-2 px-3">
            ↗ Public Atlas
          </Link>
          <button onClick={logout} className="w-full flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-red-400 py-2 px-3" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#101113]/95 backdrop-blur-sm border-b border-white/8 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setOpen(true)} className="text-white/70" data-testid="admin-mobile-menu"><Menu size={20} /></button>
        <p className="overline text-[#C17F59]">Liquid Control Room</p>
        <span className="w-5" />
      </header>

      {/* Content */}
      <main className="flex-1 md:ml-0 pt-14 md:pt-0 min-h-screen overflow-x-hidden" key={location.pathname}>
        <Outlet />
      </main>
    </div>
  );
}
